import { z } from 'zod'

/**
 * Which language a person reads the product in (PRD-27 D-1, LOC-001).
 *
 * It lives in the contract rather than only in the web app because it crosses the wire in
 * three directions: the session carries the account's choice out, `PATCH /api/me` carries a
 * new one in, and the server reads it when it writes something a person will read later.
 *
 * ── Why an enum and not a string ──
 *
 * A locale reaches a template selector, a cache key and, once there is an email transport, a
 * header. Accepting arbitrary text in any of those is injection and cache-amplification
 * waiting to happen, so the boundary is two values and the server refuses anything else
 * rather than storing it and discovering the problem later.
 *
 * `en` is first because it is the source catalogue, the type every other locale is checked
 * against, and the fallback.
 */
export const LOCALES = ['en', 'pt-BR'] as const

export const LocaleSchema = z.enum(LOCALES)
export type Locale = z.infer<typeof LocaleSchema>

export const DEFAULT_LOCALE: Locale = 'en'

export const isLocale = (value: unknown): value is Locale => LocaleSchema.safeParse(value).success

/**
 * Any Portuguese is Brazilian Portuguese here, and anything unrecognised is nothing rather
 * than a guess.
 *
 * A prefix match rather than an exact one, because a browser sends `pt`, `pt-BR`, `pt-PT` and
 * `pt-br`, and somebody whose system is set to Portugal is far better served by Brazilian
 * Portuguese than by English. If `pt-PT` ever ships this becomes a real regional match, and
 * this comment is the reason it was not one.
 */
export const normaliseLocale = (value: unknown): Locale | null => {
  if (typeof value !== 'string') return null
  const lower = value.trim().toLowerCase()
  if (!lower) return null
  if (lower.startsWith('pt')) return 'pt-BR'
  if (lower.startsWith('en')) return 'en'
  return null
}
